import { api } from '@/lib/api';
import { uploadToSignedUrl } from '@/features/documents/api';
import type { AuthoringDetail, AuthoringDraft, AuthoringFields, AuthoringPreview, AuthoringVersion } from '../types/authoring.types';
import type { ControlledDocument, EqmsSignature, EqmsTransitionInput } from '../types/eqms.types';

const BASE = '/v4/dms/authoring-drafts';
const preserveEditor = { skipAuthRedirect: true };

export const getAuthoringDrafts = () => api.get<{ drafts: AuthoringDraft[] }>(BASE);
export const deleteAuthoringDraft = (id: string, expectedLockVersion: number) =>
  api.delete<{ id: string; deleted: boolean }>(`${BASE}/${id}?${new URLSearchParams({ expectedLockVersion: String(expectedLockVersion) })}`);
export const createAuthoringDraft = (template: 'blank' | 'sop' | 'procedure' | 'glossary', language: 'en' | 'fr' | 'bilingual') =>
  api.post<{ draft: AuthoringDraft }>(BASE, { template, language,
    ...(['procedure', 'glossary'].includes(template) ? { documentType: template } : {}),
  });
/** Opens the user's own uploaded file as a prefilled blank draft; reuses an open draft of the same file. */
export const createAuthoringDraftFromUpload = (sourceDocumentId: string, language: 'en' | 'fr') =>
  api.post<{ draft: AuthoringDraft; omitted: { images: number; elements: number } | null; reused: boolean }>(BASE,
    { template: 'blank', language, sourceDocumentId });
export const getAuthoringDraft = (id: string) => api.get<AuthoringDetail>(`${BASE}/${id}`, undefined, preserveEditor);
export const saveAuthoringDraft = (id: string, fields: AuthoringFields, expectedLockVersion: number) =>
  api.patch<{ draft: AuthoringDraft }>(`${BASE}/${id}`, { ...fields, expectedLockVersion }, preserveEditor);
export const prepareAuthoringDraft = (id: string, expectedLockVersion: number) =>
  api.post<AuthoringPreview & { draft: AuthoringDraft }>(`${BASE}/${id}/prepare`, { expectedLockVersion }, { ...preserveEditor, timeoutMs: 120_000 });
export const registerAuthoringDraft = (id: string, input: EqmsTransitionInput) =>
  api.post<{ document: ControlledDocument; signature: EqmsSignature }>(`${BASE}/${id}/register`, {
    snapshotId: input.snapshotId, expectedLockVersion: input.expectedLockVersion, reason: input.reason,
    meaning: 'authorship', idempotencyKey: input.idempotencyKey, username: input.username, password: input.password,
  }, preserveEditor);
export const openControlledAuthoringDraft = (id: string, reason?: string) =>
  api.post<{ draft: AuthoringDraft }>(`/v4/dms/controlled-documents/${id}/authoring-draft`, { reason });
export const getControlledVersions = (id: string) =>
  api.get<{ versions: AuthoringVersion[] }>(`/v4/dms/controlled-documents/${id}/versions`);
export const getControlledEvidence = (id: string) =>
  api.get<Record<string, unknown>>(`/v4/dms/controlled-documents/${id}/evidence`);
export const getAuthoringAssetPreview = (draftId: string, assetId: string) =>
  api.get<{ previewUrl: string; expiresAt: string }>(`${BASE}/${draftId}/assets/${assetId}/preview-url`, undefined, preserveEditor);

export async function uploadAuthoringAsset(draftId: string, file: File) {
  const prepared = await api.post<{ assetId: string; uploadUrl: string }>(`${BASE}/${draftId}/assets/upload-url`, {
    filename: file.name, mimeType: file.type, size: file.size,
  }, preserveEditor);
  await uploadToSignedUrl(prepared.uploadUrl, file);
  return api.post<{ asset: { id: string }; previewUrl: string; expiresAt: string }>(
    `${BASE}/${draftId}/assets/${prepared.assetId}/confirm`, {}, preserveEditor,
  );
}
